var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { isOk, voidResult } from "healthex-common/result";
import { t } from "i18next";
import { observable } from "mobx";
import { Persistence, ClearFallbackData } from "../Infra/Persistence";
import { willNeverHappen } from "healthex-common/utils";
import { AxiosError } from "axios";
import { UserType } from "healthex-common/user.dtos";
import { FeatureFlag } from "healthex-common/features.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
export var SmartAuthProvider;
(function (SmartAuthProvider) {
    SmartAuthProvider["EPIC"] = "EPIC";
    SmartAuthProvider["CLEAR_IAL2"] = "CLEAR_IAL2";
})(SmartAuthProvider || (SmartAuthProvider = {}));
export var SmartAuthType;
(function (SmartAuthType) {
    SmartAuthType["PROVIDER_AUTH"] = "PROVIDER_AUTH";
    SmartAuthType["PATIENT_AUTH"] = "PATIENT_AUTH";
    SmartAuthType["PATIENT_REAUTH"] = "PATIENT_REAUTH";
    SmartAuthType["PROXY_ACCESS_REAUTH"] = "PROXY_ACCESS_REAUTH";
})(SmartAuthType || (SmartAuthType = {}));
export var SmartAuthState;
(function (SmartAuthState) {
    SmartAuthState["INACTIVE"] = "INACTIVE";
    SmartAuthState["IN_PROGRESS"] = "IN_PROGRESS";
    SmartAuthState["LOGGING_IN"] = "LOGGING_IN";
    SmartAuthState["ERROR"] = "ERROR";
})(SmartAuthState || (SmartAuthState = {}));
export var SmartAuthErrorReason;
(function (SmartAuthErrorReason) {
    SmartAuthErrorReason["LOGIN_WINDOW_CLOSED"] = "LOGIN_WINDOW_CLOSED";
    SmartAuthErrorReason["SMART_AUTH_GENERIC"] = "SMART_AUTH_GENERIC";
    SmartAuthErrorReason["SMART_AUTH_UNEXPECTED_CALLBACK"] = "SMART_AUTH_UNEXPECTED_CALLBACK";
    SmartAuthErrorReason["SMART_AUTH_INVALID_STATE_PARAM"] = "SMART_AUTH_INVALID_STATE_PARAM";
    SmartAuthErrorReason["CLEAR_AUTH_GENERIC"] = "CLEAR_AUTH_GENERIC";
    SmartAuthErrorReason["CLEAR_AUTH_UNEXPECTED_CALLBACK"] = "CLEAR_AUTH_UNEXPECTED_CALLBACK";
    SmartAuthErrorReason["CLEAR_AUTH_INVALID_STATE_PARAM"] = "CLEAR_AUTH_INVALID_STATE_PARAM";
    SmartAuthErrorReason["AUTH_FAILED"] = "AUTH_FAILED";
    SmartAuthErrorReason["PATIENT_NOT_FOUND"] = "PATIENT_NOT_FOUND";
    SmartAuthErrorReason["CLEAR_VALIDATION_FAILED"] = "CLEAR_VALIDATION_FAILED";
})(SmartAuthErrorReason || (SmartAuthErrorReason = {}));
export class SmartAuthManager {
    constructor(authService, authManager, persistence, clearService, featureService) {
        this.authService = authService;
        this.authManager = authManager;
        this.persistence = persistence;
        this.clearService = clearService;
        this.featureService = featureService;
        this.state = SmartAuthState.INACTIVE;
        this.error = null;
        this.errorReason = null;
        this.reauthSuccess = null;
        this.reauthProxyAccess = null;
    }
    async redirectToClearForOAuth(type, callbackHash, organizationId, isTryingAgain, sandboxMode) {
        const resolvedOrganizationId = typeof organizationId === "function" ? await organizationId() : organizationId;
        this.setClearIAL2CallbackHash(callbackHash);
        const url = await this.startSmartAuth(SmartAuthProvider.CLEAR_IAL2, type, this.getRedirectUri(SmartAuthProvider.CLEAR_IAL2), resolvedOrganizationId, undefined, isTryingAgain, sandboxMode);
        window.location.href = url;
    }
    async startSmartAuth(provider, type, redirectUrl, organizationId, launch, useFallback, sandboxMode) {
        this.reauthSuccess = null;
        this.reauthProxyAccess = null;
        this.state = SmartAuthState.IN_PROGRESS;
        this.persistence.setSmartAuthData(null);
        this.onError(null, null, false);
        console.log(`Starting Smart Auth session, with the ${provider} provider`);
        switch (provider) {
            case SmartAuthProvider.EPIC: {
                if (!organizationId) {
                    throw new Error("No organization id for EPIC authentication");
                }
                const urlResponse = await this.authService.getSmartAuthUrl(organizationId, redirectUrl, launch, type === SmartAuthType.PROVIDER_AUTH ? UserType.HEALTHCARE_ORGANIZATION : UserType.PATIENT);
                this.persistence.setSmartAuthData({
                    provider,
                    type,
                    state: urlResponse.state,
                    organizationId,
                    isEHRLaunch: !!launch,
                    sandboxMode,
                });
                return urlResponse.url;
            }
            case SmartAuthProvider.CLEAR_IAL2: {
                const actuallyUseFallback = await this.determineUseFallback(useFallback !== null && useFallback !== void 0 ? useFallback : false, organizationId);
                this.persistence.setClearFallbackData(actuallyUseFallback ? new ClearFallbackData({ fallbackLastUsedAt: new Date() }) : null);
                console.log(`Using IAL2 verification: ${!actuallyUseFallback}`);
                const heapGlobalEventProperties = getHeapGlobalEventProperties();
                const { state, url } = await this.clearService.getIAL2Url(redirectUrl, actuallyUseFallback, sandboxMode, heapGlobalEventProperties);
                this.persistence.setSmartAuthData({
                    provider,
                    type,
                    state,
                    organizationId,
                    useFallback: actuallyUseFallback,
                    sandboxMode,
                    heapGlobalEventProperties,
                });
                return url;
            }
            default:
                return willNeverHappen(provider);
        }
    }
    async determineUseFallback(useFallback, organizationId) {
        const features = organizationId ? await this.featureService.getFeaturesForOrganization(organizationId) : [];
        const rolloutEnabledFeature = features.find((f) => f.feature === FeatureFlag.RO_CLEAR_LOGIN_FALLBACK);
        if (!rolloutEnabledFeature) {
            return false;
        }
        if (useFallback) {
            return true;
        }
        const clearFallbackData = this.persistence.getClearFallbackData();
        if (!clearFallbackData) {
            return false;
        }
        const resetFeature = features.find((f) => f.feature === FeatureFlag.CLEAR_LOGIN_FALLBACK_RESET);
        if (resetFeature) {
            const resetDate = resetFeature.decodeConfiguration();
            if (resetDate instanceof Date && resetDate > clearFallbackData.fallbackLastUsedAt) {
                this.persistence.setClearFallbackData(null);
                console.log(`Reset to IAL2 verification`);
                return false;
            }
        }
        return true;
    }
    async onSmartAuthComplete(smartCode, state, redirectUri) {
        const smartAuthData = this.persistence.getSmartAuthData();
        if (!smartAuthData) {
            this.onError("Unexpected auth callback", SmartAuthErrorReason.SMART_AUTH_UNEXPECTED_CALLBACK, false);
            return;
        }
        const { type, state: persistedState, sandboxMode } = smartAuthData;
        if (persistedState !== state) {
            this.onError("Invalid state parameter returned by server", SmartAuthErrorReason.SMART_AUTH_INVALID_STATE_PARAM, false);
            return;
        }
        this.state = SmartAuthState.LOGGING_IN;
        try {
            if (type === SmartAuthType.PATIENT_AUTH || type === SmartAuthType.PROVIDER_AUTH) {
                const result = await this.auth(smartAuthData, smartCode, redirectUri, sandboxMode);
                if (isOk(result)) {
                    this.state = SmartAuthState.INACTIVE;
                }
                else {
                    if (result.error === "PATIENT_NOT_FOUND") {
                        this.onError(t("We couldn't find any information linking you to this healthcare provider.\n\nPlease double-check that you've selected the correct provider. If the issue continues, contact your healthcare provider for support."), SmartAuthErrorReason.PATIENT_NOT_FOUND);
                    }
                    else if (result.error === "CLEAR_VALIDATION_FAILED") {
                        this.onError(t("We could not validate your identity, please try again"), SmartAuthErrorReason.CLEAR_VALIDATION_FAILED);
                    }
                    else {
                        this.onError(t("Authentication failed"), SmartAuthErrorReason.AUTH_FAILED);
                    }
                }
            }
            else if (type === SmartAuthType.PATIENT_REAUTH || type === SmartAuthType.PROXY_ACCESS_REAUTH) {
                const result = await this.reauth(smartAuthData, smartCode, redirectUri, sandboxMode);
                if (isOk(result)) {
                    this.reauthSuccess = true;
                    if (type === SmartAuthType.PROXY_ACCESS_REAUTH && typeof result === "object" && "relatedPerson" in result) {
                        this.reauthProxyAccess = { ...result };
                    }
                    this.state = SmartAuthState.INACTIVE;
                }
                else {
                    if (result.error === "CLEAR_VALIDATION_FAILED") {
                        this.onError(t("We could not validate your identity, please try again"), SmartAuthErrorReason.CLEAR_VALIDATION_FAILED);
                    }
                    else {
                        this.onError(t("Re-authentication failed"), SmartAuthErrorReason.AUTH_FAILED);
                    }
                }
            }
            else {
                this.onError(t("Unknown authentication type"), SmartAuthErrorReason.AUTH_FAILED);
            }
        }
        catch (e) {
            console.error(e);
            const err = typeof e === "object" && e !== null && "message" in e ? String(e.message) : String(e);
            this.onError(err, SmartAuthErrorReason.AUTH_FAILED);
        }
        finally {
            this.persistence.setSmartAuthData(null);
        }
    }
    async auth(smartAuthData, smartCode, redirectUri, sandboxMode) {
        switch (smartAuthData.provider) {
            case SmartAuthProvider.EPIC:
                if (!smartCode || !redirectUri) {
                    this.onError(t("SMART on FHIR authentication failed - no smart code"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                if (!smartAuthData.organizationId) {
                    this.onError(t("SMART on FHIR authentication failed - no org id"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                try {
                    return await this.authManager.authenticateBySmartCode(smartCode, smartAuthData.type === SmartAuthType.PROVIDER_AUTH ? UserType.HEALTHCARE_ORGANIZATION : UserType.PATIENT, smartAuthData.organizationId, redirectUri, sandboxMode);
                }
                catch (e) {
                    if (e instanceof AxiosError) {
                        this.onError(t("SMART on FHIR authentication failed - invalid smart code"), SmartAuthErrorReason.AUTH_FAILED);
                        return voidResult();
                    }
                    else {
                        throw e;
                    }
                }
            case SmartAuthProvider.CLEAR_IAL2:
                if (!smartCode || !redirectUri) {
                    this.onError(t("SMART on FHIR authentication failed - no smart code"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                return await this.authManager.authenticateByClearIAL2(smartCode, redirectUri, smartAuthData.organizationId, smartAuthData.useFallback, sandboxMode, smartAuthData.heapGlobalEventProperties);
            default:
                this.onError(t("Unkown authentication provider"), SmartAuthErrorReason.AUTH_FAILED);
                return voidResult();
        }
    }
    async reauth(smartAuthData, smartCode, redirectUri, sandboxMode) {
        switch (smartAuthData.provider) {
            case SmartAuthProvider.EPIC:
                if (!smartCode || !redirectUri) {
                    this.onError(t("SMART on FHIR authentication failed - no smart code"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                if (!smartAuthData.organizationId) {
                    this.onError(t("SMART on FHIR authentication failed - no org id"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                return await this.authManager.reauthenticateBySmartCode(smartAuthData.type, smartCode, smartAuthData.organizationId, redirectUri, sandboxMode);
            case SmartAuthProvider.CLEAR_IAL2:
                if (!smartCode || !redirectUri) {
                    this.onError(t("SMART on FHIR authentication failed - no smart code"), SmartAuthErrorReason.AUTH_FAILED);
                    return voidResult();
                }
                return await this.authManager.reauthenticateByClearIAL2(smartCode, redirectUri, smartAuthData.organizationId, sandboxMode);
            default:
                this.onError(t("Unkown authentication provider in re-auth"), SmartAuthErrorReason.AUTH_FAILED);
                return voidResult();
        }
    }
    onError(error, reason, addErrorState = true) {
        this.error = error;
        if (addErrorState) {
            this.state = SmartAuthState.ERROR;
        }
        this.errorReason = reason;
    }
    async onSmartAuthError(error, reason) {
        this.onError(error, reason !== null && reason !== void 0 ? reason : SmartAuthErrorReason.SMART_AUTH_GENERIC);
        this.persistence.setSmartAuthData(null);
    }
    getRedirectUri(provider) {
        let url;
        switch (provider) {
            case SmartAuthProvider.EPIC:
                url = `${window.location.origin}/#/smart-callback`;
                break;
            case SmartAuthProvider.CLEAR_IAL2:
                url = `${window.location.origin}`;
                break;
            default:
                return willNeverHappen(provider);
        }
        return url;
    }
    static checkForClearIAL2RedirectCode() {
        const clearIAL2LoginState = new Persistence().getClearIAL2CallbackHash();
        if (!clearIAL2LoginState)
            return null;
        let queryString = window.location.search;
        if (!queryString && window.location.hash.includes("?")) {
            const hashParts = window.location.hash.split("?");
            queryString = `?${hashParts[1]}`;
        }
        const urlParams = new URLSearchParams(queryString);
        const code = urlParams.get("code");
        const scope = urlParams.get("scope");
        const state = urlParams.get("state");
        if (code && scope && state) {
            return { code, scope, state };
        }
        return null;
    }
    getClearIAL2CallbackHash() {
        return this.persistence.getClearIAL2CallbackHash();
    }
    setClearIAL2CallbackHash(callbackHash) {
        this.persistence.setClearIAL2CallbackHash(callbackHash);
    }
    resetAuth() {
        this.state = SmartAuthState.INACTIVE;
        this.error = null;
        this.errorReason = null;
        this.reauthSuccess = null;
        this.reauthProxyAccess = null;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "state", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "error", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "errorReason", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "reauthSuccess", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SmartAuthManager.prototype, "reauthProxyAccess", void 0);
